import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./AdminDashboard.css";

const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL;

const SECTIONS = {
  DEMO: "demo",
  USERS: "users",
  SUPPORT: "support",
};

const API = {
  demo: `${API_BASE_URL}/api/auth/getall`,
  users: `${API_BASE_URL}/api/auth/getallusers`,
  tickets: `${API_BASE_URL}/api/auth/tickets`,
  ticketStatus: (id) => `${API_BASE_URL}/api/auth/tickets/${id}`,
};

const AdminDashboard = () => {
  const navigate = useNavigate();

  const [currentSection, setCurrentSection] = useState(SECTIONS.DEMO);

  const [demoRequests, setDemoRequests] = useState([]);
  const [users, setUsers] = useState([]);

  // ---- Support Tickets state ----
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketQuery, setTicketQuery] = useState("");
  const [error, setError] = useState("");

  const fmt = (d) =>
    new Date(d).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });

  const debouncedQuery = useMemo(
    () => ticketQuery.trim().toLowerCase(),
    [ticketQuery]
  );

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const u = JSON.parse(storedUser);
      if (u.email !== "admin@gmail.com") navigate("/");
    } else {
      navigate("/SignIn");
    }
  }, [navigate]);

  const token = localStorage.getItem("token");

  const authFetch = useCallback((url, options = {}) => {
    if (!token) {
      navigate("/SignIn");
      return Promise.reject("No auth token");
    }

    return fetch(url, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });
  }, [token, navigate]);

  useEffect(() => {
    if (currentSection === SECTIONS.DEMO) {
      fetch(API.demo)
        .then((res) => res.json())
        .then(setDemoRequests)
        .catch(console.error);
    }

    if (currentSection === SECTIONS.USERS) {
      authFetch(API.users)
        .then((res) => res.json())
        .then(setUsers)
        .catch(console.error);
    }
  }, [currentSection, authFetch]);

  useEffect(() => {
    if (currentSection !== SECTIONS.SUPPORT) return;

    const ctrl = new AbortController();
    const q = debouncedQuery
      ? `?email=${encodeURIComponent(debouncedQuery)}`
      : "";

    setTicketsLoading(true);
    setError("");

    fetch(`${API.tickets}${q}`, { signal: ctrl.signal })
      .then(async (res) => {
        if (!res.ok)
          throw new Error((await res.text()) || "Failed to fetch tickets");
        return res.json();
      })
      .then((data) =>
        setTickets(Array.isArray(data) ? data : data.items || [])
      )
      .catch((e) => {
        if (e.name !== "AbortError")
          setError(e.message || "Failed to fetch tickets");
      })
      .finally(() => setTicketsLoading(false));

    return () => ctrl.abort();
  }, [currentSection, debouncedQuery]);

  const toggleStatus = async (t) => {
    try {
      const next = t.status === "open" ? "closed" : "open";
      const res = await fetch(API.ticketStatus(t._id), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: next }),
      });
      if (!res.ok) throw new Error("Update failed");
      setTickets((prev) =>
        prev.map((x) => (x._id === t._id ? { ...x, status: next } : x))
      );
    } catch (e) {
      alert(e.message || "Could not update status");
    }
  };

  const updateUserStatus = async (userId, status, days) => {
    try {
      const res = await authFetch(
        `${API_BASE_URL}/api/auth/admin/users/${userId}/status`,
        {
          method: "PATCH",
          body: JSON.stringify({ status, days }),
        }
      );

      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || "Failed to update user");
      }

      const data = await res.json();

      setUsers((prev) =>
        prev.map((u) =>
          u._id === userId
            ? {
              ...u,
              status: data.user.status,
              suspendedUntil: data.user.suspendedUntil,
            }
            : u
        )
      );
    } catch (err) {
      alert(err.message || "Action failed");
    }
  };

  const markDemoCompleted = async (demoId, demoGivenBy) => {
    try {
      const res = await authFetch(
        `${API_BASE_URL}/api/auth/admin/demo/${demoId}/mark-completed`,
        {
          method: "PATCH",
          body: JSON.stringify({ demoGivenBy }),
        }
      );

      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || "Failed to mark demo completed");
      }

      const data = await res.json();

      setDemoRequests((prev) =>
        prev.map((d) =>
          d._id === demoId ? data.demo : d
        )
      );
    } catch (err) {
      alert(err.message || "Action failed");
    }
  };


  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("redirectAfterLogin");
    navigate("/SignIn");
  };

  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [suspendDays, setSuspendDays] = useState("3");
  const [selectedUserId, setSelectedUserId] = useState(null);

  // Modal for Marking Demo Completed
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [demoGivenBy, setDemoGivenBy] = useState("");
  const [selectedDemoId, setSelectedDemoId] = useState(null);

  // Modal for Making Ban User
  const [showBanModal, setShowBanModal] = useState(false);
  const [banUserId, setBanUserId] = useState(null);


  const [activeTicket, setActiveTicket] = useState(null);

  return (
    <>
      <div className="admin-dash-container">
        <aside className="admin-dash-sidebar">
          <div className="admin-dash-sidebar-header">
            <h2>Admin Panel</h2>
          </div>
          <ul className="admin-dash-nav">
            <li
              onClick={() => setCurrentSection(SECTIONS.DEMO)}
              className={`admin-dash-nav-item ${currentSection === SECTIONS.DEMO ? "active" : ""}`}
            >
              📄 View Demo Requests
            </li>
            <li
              onClick={() => setCurrentSection(SECTIONS.USERS)}
              className={`admin-dash-nav-item ${currentSection === SECTIONS.USERS ? "active" : ""}`}
            >
              👥 Manage Users
            </li>
            <li
              onClick={() => setCurrentSection(SECTIONS.SUPPORT)}
              className={`admin-dash-nav-item ${currentSection === SECTIONS.SUPPORT ? "active" : ""}`}
            >
              🎫 Support Tickets
            </li>
          </ul>
          <div className="admin-dash-sidebar-footer">
            <button className="btn btn-logout" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </aside>

        <main className="admin-dash-main">
          {currentSection === SECTIONS.DEMO && (
            <>
              <h2>Demo Requests</h2>
              <div className="admin-dash-demo-list">
                {demoRequests.length > 0 ? (
                  demoRequests.map((demo) => (
                    <div key={demo._id} className="admin-dash-demo-card">
                      <h4>
                        {demo.firstName} {demo.lastName}
                      </h4>

                      {/* STATUS BADGE */}
                      <span className={`user-status ${demo.demoStatus || "pending"}`}>
                        {demo.demoStatus || "pending"}
                      </span>

                      <p>Email: {demo.email}</p>
                      <p>Company: {demo.company}</p>
                      <p>Job Title: {demo.jobTitle}</p>
                      <p>Country: {demo.country}</p>
                      <p>Monthly Queries: {demo.monthlyQueries}</p>
                      <p>Budget: {demo.budget}</p>

                      {/* COMPLETED INFO */}
                      {demo.demoStatus === "completed" && (
                        <div style={{ marginTop: 8 }}>
                          <p><strong>Demo Given By:</strong> {demo.demoGivenBy}</p>
                          <p className="muted">
                            {fmt(demo.demoGivenAt)}
                          </p>
                        </div>
                      )}

                      {/* ACTION */}
                      {demo.demoStatus !== "completed" && (
                        <button
                          className="btn btn-open"
                          style={{ marginTop: 10 }}
                          onClick={() => {
                            setSelectedDemoId(demo._id);
                            setDemoGivenBy("");
                            setShowDemoModal(true);
                          }}

                        >
                          Mark Demo Given
                        </button>
                      )}
                    </div>

                  ))
                ) : (
                  <p>No demo requests yet.</p>
                )}
              </div>
            </>
          )}

          {currentSection === SECTIONS.USERS && (
            <>
              <h2>Manage Users</h2>
              <div className="admin-dash-user-list">
                {users.length > 0 ? (
                  users.map((u) => (
                    <div key={u._id} className="admin-dash-user-card">
                      <h4>{u.name}</h4>
                      <p>Email: {u.email}</p>

                      {/* STATUS BADGE */}
                      <span className={`user-status ${u.status || "active"}`}>
                        {u.status || "active"}
                      </span>

                      <div className="user-actions">
                        {u.status !== "banned" && (
                          <button
                            className="btn btn-open"
                            onClick={() => {
                              setSelectedUserId(u._id);
                              setSuspendDays("3");
                              setShowSuspendModal(true);
                            }}
                          >
                            Suspend
                          </button>
                        )}

                        {u.status === "suspended" && (
                          <button
                            className="btn btn-open"
                            onClick={() => updateUserStatus(u._id, "active")}
                          >
                            Reactivate
                          </button>
                        )}

                        {u.status !== "banned" && (
                          <button
                            className="btn btn-close"
                            onClick={() => {
                              setBanUserId(u._id);
                              setShowBanModal(true);
                            }}
                          >
                            Ban
                          </button>

                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p>No users found.</p>
                )}
              </div>
            </>
          )}


          {currentSection === SECTIONS.SUPPORT && (
            <>
              <div className="support-head">
                <h2>Support Tickets</h2>
                <div className="support-toolbar">
                  <input
                    className="support-search"
                    type="search"
                    placeholder="Search by user email…"
                    value={ticketQuery}
                    onChange={(e) => setTicketQuery(e.target.value)}
                  />
                  <span className="support-count">
                    {ticketsLoading ? "Loading…" : `${tickets.length} ticket(s)`}
                  </span>
                </div>
              </div>

              {error && <div className="support-error">{error}</div>}

              <div className="ticket-grid">
                {!ticketsLoading && tickets.length === 0 && (
                  <p>No tickets found.</p>
                )}

                {tickets.map((t) => (
                  <article key={t._id} className="ticket-card">
                    <header className="ticket-header">
                      <div className="ticket-subject">{t.subject}</div>
                      <span
                        className={`ticket-badge ${t.status === "open" ? "open" : "closed"
                          }`}
                      >
                        {t.status}
                      </span>
                    </header>

                    <div className="ticket-meta">
                      <div>
                        <strong>{t.user?.name || "Anonymous"}</strong>
                        <div className="muted">{t.user?.email || "—"}</div>
                      </div>
                      <div className="muted">{fmt(t.createdAt)}</div>
                    </div>

                    <p className="ticket-message">{t.message}</p>

                    <footer className="ticket-actions">
                      <button
                        className="btn-ghost"
                        onClick={() => navigator.clipboard.writeText(t._id)}
                      >
                        Copy ID
                      </button>

                      <button
                        className="btn-ghost"
                        onClick={() => setActiveTicket(t)}
                      >
                        View
                      </button>

                      <div className="spacer" />

                      <button
                        className={`btn ${t.status === "open" ? "btn-close" : "btn-open"}`}
                        onClick={() => toggleStatus(t)}
                      >
                        {t.status === "open" ? "Mark closed" : "Reopen"}
                      </button>
                    </footer>

                  </article>
                ))}
              </div>
            </>
          )}
          {activeTicket && (
            <div className="ticket-modal-backdrop" onClick={() => setActiveTicket(null)}>
              <div
                className="ticket-modal"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <header className="ticket-modal-header">
                  <div>
                    <h3 className="ticket-modal-title">{activeTicket.subject}</h3>
                    <span
                      className={`ticket-badge ${activeTicket.status === "open" ? "open" : "closed"
                        }`}
                    >
                      {activeTicket.status}
                    </span>
                  </div>

                  <button
                    className="ticket-modal-close"
                    onClick={() => setActiveTicket(null)}
                  >
                    ✕
                  </button>
                </header>

                {/* User Info */}
                <section className="ticket-user-info">
                  <div className="info-row">
                    <span className="label">Name</span>
                    <span className="value">
                      {activeTicket.user?.name || "Anonymous"}
                    </span>
                  </div>

                  <div className="info-row">
                    <span className="label">Email</span>
                    <span className="value">
                      {activeTicket.user?.email || "—"}
                    </span>
                  </div>

                  <div className="info-row">
                    <span className="label">Ticket ID</span>
                    <span className="value mono">{activeTicket._id}</span>
                  </div>

                  <div className="info-row">
                    <span className="label">Created</span>
                    <span className="value">{fmt(activeTicket.createdAt)}</span>
                  </div>
                </section>

                {/* Message */}
                <section className="ticket-message-full">
                  <h4>Message</h4>
                  <p>{activeTicket.message}</p>
                </section>

                {/* Actions */}
                <footer className="ticket-modal-footer">
                  <button
                    className={`btn ${activeTicket.status === "open" ? "btn-close" : "btn-open"
                      }`}
                    onClick={() => toggleStatus(activeTicket)}
                  >
                    {activeTicket.status === "open" ? "Mark closed" : "Reopen"}
                  </button>
                </footer>
              </div>
            </div>
          )}

        </main>
      </div>
      {/* Suspend User Modal */}
      {showSuspendModal && (
        <div className="modal-overlay" onClick={() => setShowSuspendModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Suspend User</h3>

            <input
              type="number"
              min="1"
              value={suspendDays}
              onChange={(e) => setSuspendDays(e.target.value)}
            />

            <div className="modal-actions">
              <button
                className="btn btn-cancel"
                onClick={() => setShowSuspendModal(false)}
              >
                Cancel
              </button>

              <button
                className="btn btn-open"
                onClick={() => {
                  updateUserStatus(
                    selectedUserId,
                    "suspended",
                    Number(suspendDays)
                  );
                  setShowSuspendModal(false);
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mark Demo Completed Modal */}
      {showDemoModal && (
        <div className="modal-overlay" onClick={() => setShowDemoModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Mark Demo Completed</h3>

            <input
              type="text"
              placeholder="Demo given by"
              value={demoGivenBy}
              onChange={(e) => setDemoGivenBy(e.target.value)}
            />

            <div className="modal-actions">
              <button
                className="btn btn-cancel"
                onClick={() => setShowDemoModal(false)}
              >
                Cancel
              </button>

              <button
                className="btn btn-open"
                disabled={!demoGivenBy.trim()}
                onClick={() => {
                  markDemoCompleted(
                    selectedDemoId,
                    demoGivenBy.trim()
                  );
                  setShowDemoModal(false);
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Banning User */}
      {showBanModal && (
        <div className="modal-overlay" onClick={() => setShowBanModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: "#c0392b" }}>Ban User</h3>

            <p>
              This action is <strong>permanent</strong>.
              The user will no longer be able to access the system.
            </p>

            <div className="modal-actions">
              <button
                className="btn btn-cancel"
                onClick={() => setShowBanModal(false)}
              >
                Cancel
              </button>

              <button
                className="btn btn-close"
                onClick={() => {
                  updateUserStatus(banUserId, "banned");
                  setShowBanModal(false);
                }}
              >
                Yes, Ban User
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminDashboard;